import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DOMPurify from "dompurify";
import { useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import "./style.scss";
import { ACTIVATION_CACHE_TIME, getActivationDescribe } from "@api/activation";
import Button from "@components/Button";
import PagesHeader from "@components/PagesHeader";
import changeIcon from "@assets/change.svg";
import deleteIcon from "@assets/delete.svg";
import stopIcon from "@assets/stop.svg";
import playIcon from "@assets/play.svg";
import repeatIcon from "@assets/repeat.svg";
import infoIcon from "@assets/info.svg";
import closeIcon from "@assets/close.svg";
import NiceModal from "@ebay/nice-modal-react";
import animation from "./img/animation-600px.mp4";

const DEFAULT_BPM = 125;
const BPM_OPTIONS = [115, 125, 135];
const START_SCHEDULE_AHEAD = 0.12;
const FADE_TIME = 0.01;

function normalizeAudioUrl(url) {
  if (!url || typeof url !== "string") return url;

  // API payloads can contain windows-style separators and pre-encoded values.
  let normalized = url.replace(/\\/g, "/").trim();

  // Avoid double-encoding cases like %2520 -> %20.
  try {
    normalized = decodeURIComponent(normalized);
  } catch (_) {
    // noop
  }

  // On Amplify app can be hosted under a subpath, so root-relative assets
  // like /assets/... should respect Vite base URL.
  const baseUrl = import.meta.env.BASE_URL || "/";
  if (normalized.startsWith("/assets/") && baseUrl !== "/") {
    normalized = `${baseUrl.replace(/\/$/, "")}${normalized}`;
  }

  return normalized;
}

async function fetchAndDecodeAudio(context, url) {
  const normalizedUrl = normalizeAudioUrl(url);
  const response = await fetch(normalizedUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch audio (${response.status}) for: ${normalizedUrl}`,
    );
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.toLowerCase().includes("audio")) {
    throw new Error(
      `Invalid audio response for ${normalizedUrl}. Content-Type: ${contentType || "unknown"}`,
    );
  }

  const arrayBuffer = await response.arrayBuffer();
  try {
    return await context.decodeAudioData(arrayBuffer);
  } catch (error) {
    throw new Error(
      `Unable to decode audio data for ${normalizedUrl}. ${error?.message || ""}`.trim(),
    );
  }
}

function useDjAudioEngine({ tracks }) {
  const audioContextRef = useRef(null);
  const masterGainRef = useRef(null);

  const buffersRef = useRef(new Map()); // key: src
  const gainsRef = useRef(new Map()); // key: trackId
  const sourcesRef = useRef(new Map()); // key: trackId

  const transportAnchorContextTimeRef = useRef(0);
  const transportAnchorOffsetRef = useRef(0);
  const transportRunningRef = useRef(false);

  const [isReady, setIsReady] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const allTracks = useMemo(() => {
    return [...tracks.bass, ...tracks.kick, ...tracks.drums, ...tracks.sfx];
  }, [tracks]);

  const getTrackById = useCallback(
    (trackId) => allTracks.find((track) => track.id === trackId),
    [allTracks],
  );

  const getContext = useCallback(() => {
    if (!audioContextRef.current) {
      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;

      if (!AudioContextClass) {
        throw new Error("Web Audio API is not supported in this browser");
      }

      const context = new AudioContextClass();

      const masterGain = context.createGain();
      masterGain.gain.value = 1;
      masterGain.connect(context.destination);

      audioContextRef.current = context;
      masterGainRef.current = masterGain;
    }

    return audioContextRef.current;
  }, []);

  const unlockAudio = useCallback(async () => {
    const context = getContext();

    if (context.state === "suspended") {
      await context.resume();
    }

    const buffer = context.createBuffer(1, 1, context.sampleRate);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);

    try {
      source.start(0);
    } catch (_) {
      // noop
    }

    try {
      source.disconnect();
    } catch (_) {
      // noop
    }

    return context;
  }, [getContext]);

  const ensureResumed = useCallback(async () => {
    const context = getContext();

    if (context.state === "suspended") {
      await context.resume();
    }

    return context;
  }, [getContext]);

  const ensureGainNode = useCallback(
    (trackId) => {
      const context = getContext();

      if (!gainsRef.current.has(trackId)) {
        const gain = context.createGain();
        gain.gain.value = 0;
        gain.connect(masterGainRef.current);
        gainsRef.current.set(trackId, gain);
      }

      return gainsRef.current.get(trackId);
    },
    [getContext],
  );

  const stopSource = useCallback((trackId) => {
    const source = sourcesRef.current.get(trackId);
    if (!source) return;

    try {
      source.stop();
    } catch (_) {
      // noop
    }

    try {
      source.disconnect();
    } catch (_) {
      // noop
    }

    sourcesRef.current.delete(trackId);
  }, []);

  const getTransportElapsed = useCallback(() => {
    const context = audioContextRef.current;
    if (!context || !transportRunningRef.current) {
      return transportAnchorOffsetRef.current || 0;
    }

    return Math.max(
      0,
      transportAnchorOffsetRef.current +
        (context.currentTime - transportAnchorContextTimeRef.current),
    );
  }, []);

  const preloadAll = useCallback(async () => {
    if (!allTracks.length) {
      setLoadingProgress(100);
      setIsReady(true);
      return;
    }

    const context = getContext();
    let loaded = 0;
    const total = allTracks.length;

    setIsReady(false);
    setLoadingProgress(0);

    await Promise.all(
      allTracks.map(async (track) => {
        if (buffersRef.current.has(track.src)) {
          ensureGainNode(track.id);
          loaded += 1;
          setLoadingProgress(Math.round((loaded / total) * 100));
          return;
        }

        const buffer = await fetchAndDecodeAudio(context, track.src);
        buffersRef.current.set(track.src, buffer);
        ensureGainNode(track.id);

        loaded += 1;
        setLoadingProgress(Math.round((loaded / total) * 100));
      }),
    );

    setLoadingProgress(100);
    setIsReady(true);
  }, [allTracks, ensureGainNode, getContext]);

  const createLoopSource = useCallback(
    ({ trackId, startAtTime, offsetSeconds = 0 }) => {
      const context = audioContextRef.current;
      if (!context) return null;

      const track = getTrackById(trackId);
      if (!track) return null;

      const buffer = buffersRef.current.get(track.src);
      if (!buffer) return null;

      const gain = ensureGainNode(trackId);

      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.loopStart = 0;
      source.loopEnd = buffer.duration;
      source.playbackRate.value = 1;
      source.connect(gain);

      const duration = buffer.duration || 0;
      const normalizedOffset = duration > 0 ? offsetSeconds % duration : 0;

      source.start(startAtTime, normalizedOffset);
      sourcesRef.current.set(trackId, source);

      return source;
    },
    [ensureGainNode, getTrackById],
  );

  const stopAllSources = useCallback(() => {
    sourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch (_) {
        // noop
      }

      try {
        source.disconnect();
      } catch (_) {
        // noop
      }
    });

    sourcesRef.current.clear();
  }, []);

  const setTrackMuted = useCallback(
    ({ trackId, muted }) => {
      if (!trackId || !audioContextRef.current) return;

      const context = audioContextRef.current;
      const gain = ensureGainNode(trackId);

      gain.gain.cancelScheduledValues(context.currentTime);
      gain.gain.setValueAtTime(gain.gain.value, context.currentTime);
      gain.gain.linearRampToValueAtTime(
        muted ? 0 : 1,
        context.currentTime + FADE_TIME,
      );
    },
    [ensureGainNode],
  );

  const hasMissingBuffers = useCallback(() => {
    return allTracks.some((track) => !buffersRef.current.has(track.src));
  }, [allTracks]);

  const startTransport = useCallback(
    async ({ mutedTrackIds = [], preserveOffset = 0 }) => {
      await ensureResumed();

      if (!isReady || hasMissingBuffers()) {
        await preloadAll();
      }

      const context = audioContextRef.current;
      const mutedSet = new Set(mutedTrackIds);

      stopAllSources();

      transportAnchorOffsetRef.current = Math.max(0, preserveOffset || 0);
      transportAnchorContextTimeRef.current =
        context.currentTime + START_SCHEDULE_AHEAD;
      transportRunningRef.current = true;

      allTracks.forEach((track) => {
        const gain = ensureGainNode(track.id);
        gain.gain.cancelScheduledValues(context.currentTime);
        gain.gain.setValueAtTime(0, context.currentTime);
      });

      allTracks.forEach((track) => {
        createLoopSource({
          trackId: track.id,
          startAtTime: transportAnchorContextTimeRef.current,
          offsetSeconds: transportAnchorOffsetRef.current,
        });
      });

      allTracks.forEach((track) => {
        const gain = ensureGainNode(track.id);
        const targetValue = mutedSet.has(track.id) ? 0 : 1;

        gain.gain.cancelScheduledValues(context.currentTime);
        gain.gain.setValueAtTime(0, context.currentTime);
        gain.gain.setValueAtTime(0, transportAnchorContextTimeRef.current);
        gain.gain.linearRampToValueAtTime(
          targetValue,
          transportAnchorContextTimeRef.current + FADE_TIME,
        );
      });

      setIsStarted(true);
    },
    [
      allTracks,
      createLoopSource,
      ensureGainNode,
      ensureResumed,
      hasMissingBuffers,
      isReady,
      preloadAll,
      stopAllSources,
    ],
  );

  const ensureStarted = useCallback(
    async ({ mutedTrackIds = [] } = {}) => {
      await ensureResumed();

      if (!isReady || hasMissingBuffers()) {
        await preloadAll();
      }

      if (!transportRunningRef.current) {
        await startTransport({
          mutedTrackIds,
          preserveOffset: 0,
        });
      }
    },
    [ensureResumed, hasMissingBuffers, isReady, preloadAll, startTransport],
  );

  const restartSynced = useCallback(
    async ({ mutedTrackIds = [] } = {}) => {
      const preserveOffset = getTransportElapsed();

      await startTransport({
        mutedTrackIds,
        preserveOffset,
      });
    },
    [getTransportElapsed, startTransport],
  );

  const stopAll = useCallback(() => {
    const currentOffset = getTransportElapsed();

    stopAllSources();

    gainsRef.current.forEach((gain) => {
      try {
        gain.gain.cancelScheduledValues(0);
        gain.gain.value = 0;
      } catch (_) {
        // noop
      }
    });

    transportRunningRef.current = false;
    transportAnchorOffsetRef.current = currentOffset;
    setIsStarted(false);
  }, [getTransportElapsed, stopAllSources]);

  useEffect(() => {
    return () => {
      stopAll();

      gainsRef.current.forEach((gain) => {
        try {
          gain.disconnect();
        } catch (_) {
          // noop
        }
      });

      if (masterGainRef.current) {
        try {
          masterGainRef.current.disconnect();
        } catch (_) {
          // noop
        }
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopAll]);

  return {
    isReady,
    isStarted,
    loadingProgress,
    preloadAll,
    ensureStarted,
    setTrackMuted,
    stopAll,
    restartSynced,
    unlockAudio,
  };
}

export default function DjConsole({
  currentQuestion,
  setCurrentQuestion,
  setAnswers,
  answers,
  sendAnswers,
}) {
  const { ident } = useParams();
  const queryClient = useQueryClient();

  const [openedDeck, setOpenedDeck] = useState(null);
  const [selected, setSelected] = useState({
    bass: null,
    kick: null,
    drums1: null,
    drums2: null,
    sfx: [],
  });
  const [pausedDecks, setPausedDecks] = useState({
    bass: false,
    kick: false,
    drums1: false,
    drums2: false,
  });

  const [tempo, setTempo] = useState(DEFAULT_BPM);
  const [isSwitchingTempo, setIsSwitchingTempo] = useState(false);
  const [isUnlockingAudio, setIsUnlockingAudio] = useState(false);

  const decksAreaRef = useRef(null);
  const previousBpmKeyRef = useRef(`${DEFAULT_BPM}bpm`);
  const audioUnlockedRef = useRef(false);

  const { data: APIData } = useQuery({
    queryKey: ["activation-describe", ident],
    queryFn: () =>
      getActivationDescribe({
        activation: {
          ident,
          withSolutions: true,
        },
      }),
    retry: false,
    refetchOnMount: "always",
    staleTime: 1000 * 60 * 3,
    cacheTime: ACTIVATION_CACHE_TIME,
  });
  const { data: localGameQuestionsData } = useQuery({
    queryKey: ["game-questions", ident],
    queryFn: async () => {
      const data = await import(`../../Idents/${ident}/data.js`);
      return data.gameData;
    },
    retry: false,
    staleTime: 1000 * 60 * 60,
    cacheTime: ACTIVATION_CACHE_TIME,
  });

  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);

  const gameData =
    localGameQuestionsData || gameQuestionsQuery || APIData?.gameQuestionsQuery || {};
  const bpmKey = `${tempo}bpm`;
  const allTracksByBpm = gameData?.content?.[1]?.tracks || {};

  const tracks = allTracksByBpm[bpmKey] || {
    bass: [],
    kick: [],
    drums: [],
    sfx: [],
  };

  const {
    isReady,
    loadingProgress,
    preloadAll,
    ensureStarted,
    setTrackMuted,
    stopAll,
    restartSynced,
    unlockAudio,
  } = useDjAudioEngine({
    tracks,
  });

  const ensureAudioUnlocked = useCallback(async () => {
    if (audioUnlockedRef.current) return true;
    if (isUnlockingAudio) return false;

    setIsUnlockingAudio(true);

    try {
      await unlockAudio();
      audioUnlockedRef.current = true;
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setIsUnlockingAudio(false);
    }
  }, [isUnlockingAudio, unlockAudio]);

  const getMutedTrackIdsFromSelection = useCallback(
    (selection) => {
      const unmutedSet = new Set(
        [
          selection.bass,
          selection.kick,
          selection.drums1,
          selection.drums2,
          ...selection.sfx,
        ].filter(Boolean),
      );

      return [...tracks.bass, ...tracks.kick, ...tracks.drums, ...tracks.sfx]
        .map((track) => track.id)
        .filter((trackId) => !unmutedSet.has(trackId));
    },
    [tracks],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!decksAreaRef.current) return;
      if (!decksAreaRef.current.contains(event.target)) {
        setOpenedDeck(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      stopAll();
    };
  }, [stopAll]);

  useEffect(() => {
    const previousBpmKey = previousBpmKeyRef.current;

    if (previousBpmKey === bpmKey) {
      return;
    }

    previousBpmKeyRef.current = bpmKey;

    const switchTempo = async () => {
      setIsSwitchingTempo(true);

      try {
        if (!audioUnlockedRef.current) return;

        const mutedTrackIds = getMutedTrackIdsFromSelection(selected);

        await restartSynced({
          mutedTrackIds,
        });

        setPausedDecks({
          bass: false,
          kick: false,
          drums1: false,
          drums2: false,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsSwitchingTempo(false);
      }
    };

    switchTempo();
  }, [bpmKey, getMutedTrackIdsFromSelection, restartSynced, selected]);

  const handleDeckSelect = async (deck, trackId) => {
    if (isSwitchingTempo || isUnlockingAudio) return;

    const audioUnlocked = await ensureAudioUnlocked();
    if (!audioUnlocked) return;

    const currentTrackId =
      deck === "bass"
        ? selected.bass
        : deck === "kick"
          ? selected.kick
          : deck === "drums1"
            ? selected.drums1
            : deck === "drums2"
              ? selected.drums2
              : null;

    if (deck === "drums1" && selected.drums2 === trackId) return;
    if (deck === "drums2" && selected.drums1 === trackId) return;

    const nextTrackId = currentTrackId === trackId ? null : trackId;

    const nextSelection = {
      ...selected,
      ...(deck === "bass" ? { bass: nextTrackId } : {}),
      ...(deck === "kick" ? { kick: nextTrackId } : {}),
      ...(deck === "drums1" ? { drums1: nextTrackId } : {}),
      ...(deck === "drums2" ? { drums2: nextTrackId } : {}),
    };

    const mutedTrackIds = getMutedTrackIdsFromSelection(nextSelection);

    await ensureStarted({ mutedTrackIds });

    if (currentTrackId) {
      setTrackMuted({
        trackId: currentTrackId,
        muted: true,
      });
    }

    if (nextTrackId) {
      setTrackMuted({
        trackId: nextTrackId,
        muted: false,
      });
    }

    setSelected((prev) => {
      if (deck === "bass") return { ...prev, bass: nextTrackId };
      if (deck === "kick") return { ...prev, kick: nextTrackId };
      if (deck === "drums1") return { ...prev, drums1: nextTrackId };
      if (deck === "drums2") return { ...prev, drums2: nextTrackId };
      return prev;
    });

    setPausedDecks((prev) => ({
      ...prev,
      [deck]: false,
    }));

    setOpenedDeck(null);
  };

  const handleRemoveDeckSelection = (deck) => {
    if (isSwitchingTempo || isUnlockingAudio) return;

    const currentTrackId =
      deck === "bass"
        ? selected.bass
        : deck === "kick"
          ? selected.kick
          : deck === "drums1"
            ? selected.drums1
            : deck === "drums2"
              ? selected.drums2
              : null;

    if (currentTrackId) {
      setTrackMuted({
        trackId: currentTrackId,
        muted: true,
      });
    }

    setSelected((prev) => {
      if (deck === "bass") return { ...prev, bass: null };
      if (deck === "kick") return { ...prev, kick: null };
      if (deck === "drums1") return { ...prev, drums1: null };
      if (deck === "drums2") return { ...prev, drums2: null };
      return prev;
    });

    setPausedDecks((prev) => ({
      ...prev,
      [deck]: false,
    }));

    setOpenedDeck(null);
  };

  const handleToggleDeckMute = async (deck) => {
    if (isSwitchingTempo || isUnlockingAudio) return;

    const audioUnlocked = await ensureAudioUnlocked();
    if (!audioUnlocked) return;

    const trackId =
      deck === "bass"
        ? selected.bass
        : deck === "kick"
          ? selected.kick
          : deck === "drums1"
            ? selected.drums1
            : deck === "drums2"
              ? selected.drums2
              : null;

    if (!trackId) return;

    await ensureStarted({
      mutedTrackIds: getMutedTrackIdsFromSelection(selected),
    });

    const nextMuted = !pausedDecks[deck];

    setTrackMuted({
      trackId,
      muted: nextMuted,
    });

    setPausedDecks((prev) => ({
      ...prev,
      [deck]: nextMuted,
    }));
  };

  const handleOpenDeckOptions = (deck) => {
    if (isSwitchingTempo || isUnlockingAudio) return;
    setOpenedDeck((prev) => (prev === deck ? null : deck));
  };

  const handleSfxToggle = async (trackId) => {
    if (isSwitchingTempo || isUnlockingAudio) return;

    const audioUnlocked = await ensureAudioUnlocked();
    if (!audioUnlocked) return;

    const nextSelection = {
      ...selected,
      sfx: selected.sfx.includes(trackId)
        ? selected.sfx.filter((id) => id !== trackId)
        : selected.sfx.length >= 4
          ? selected.sfx
          : [...selected.sfx, trackId],
    };

    const exists = selected.sfx.includes(trackId);
    if (!exists && selected.sfx.length >= 4) return;

    const mutedTrackIds = getMutedTrackIdsFromSelection(nextSelection);

    await ensureStarted({ mutedTrackIds });

    setTrackMuted({
      trackId,
      muted: exists,
    });

    setSelected((prev) => {
      if (exists) {
        return {
          ...prev,
          sfx: prev.sfx.filter((id) => id !== trackId),
        };
      }

      return {
        ...prev,
        sfx: [...prev.sfx, trackId],
      };
    });
  };

  const resetAll = () => {
    stopAll();

    setSelected({
      bass: null,
      kick: null,
      drums1: null,
      drums2: null,
      sfx: [],
    });

    setPausedDecks({
      bass: false,
      kick: false,
      drums1: false,
      drums2: false,
    });

    setOpenedDeck(null);
    setTempo(DEFAULT_BPM);
    setIsSwitchingTempo(false);
  };

  const isDrumsTrackDisabled = (slot, trackId) => {
    if (slot === "drums1") return selected.drums2 === trackId;
    if (slot === "drums2") return selected.drums1 === trackId;
    return false;
  };

  const isSfxDisabled = (trackId) => {
    return !selected.sfx.includes(trackId) && selected.sfx.length >= 4;
  };

  const getDeckLabel = (deckKey) => {
    if (deckKey === "bass") {
      return (
        tracks.bass.find((item) => item.id === selected.bass)?.name ||
        "HINZUFÜGEN"
      );
    }

    if (deckKey === "kick") {
      return (
        tracks.kick.find((item) => item.id === selected.kick)?.name ||
        "HINZUFÜGEN"
      );
    }

    if (deckKey === "drums1") {
      return (
        tracks.drums.find((item) => item.id === selected.drums1)?.name ||
        "HINZUFÜGEN"
      );
    }

    if (deckKey === "drums2") {
      return (
        tracks.drums.find((item) => item.id === selected.drums2)?.name ||
        "HINZUFÜGEN"
      );
    }

    return "HINZUFÜGEN";
  };

  const handleTempoChange = (bpm) => {
    if (bpm === tempo || isSwitchingTempo || isUnlockingAudio) return;
    setTempo(bpm);
  };

  function DeckCircle({
    deckKey,
    title,
    value,
    active,
    isOpen,
    isPaused,
    onOpen,
    onRemove,
    onTogglePause,
    children,
  }) {
    const isSelected = value !== "HINZUFÜGEN";
    const isDisabled = isSwitchingTempo || isUnlockingAudio;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const [iosModalSeen, setIosModalSeen] = useState(
      false || localStorage.getItem("djConsoleIosModalSeen") === "true",
    );

    const handleCloseDeckOptions = () => {
      if (isSwitchingTempo || isUnlockingAudio) return;
      setOpenedDeck(null);
    };

    if (isIOS && !iosModalSeen) {
      NiceModal.show("dj-console-modal", { content: "ios-info" });
      setIosModalSeen(true);
      localStorage.setItem("djConsoleIosModalSeen", "true");
    }

    return (
      <div className={`deck-circle-wrapper ${isOpen ? "open" : ""} ${deckKey}`}>
        <div
          className={`deck-circle ${active ? "active" : ""} ${
            isSelected ? "selected" : ""
          } ${isPaused ? "paused" : ""} ${isDisabled ? "disabled" : ""}`}
          onClick={() => {
            if (!isSelected && !isDisabled) {
              onOpen();
            }
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (
              (event.key === "Enter" || event.key === " ") &&
              !isSelected &&
              !isDisabled
            ) {
              onOpen();
            }
          }}
        >
          {!isSelected && <div className="deck-title">{title}</div>}
          {isSelected && <div className="deck-selected-name">{value}</div>}

          {isSelected && !isPaused && (
            <video
              autoPlay
              loop
              muted
              playsInline
              src={animation}
              className="deck-wave"
            />
          )}

          <button
            type="button"
            className="deck-center"
            disabled={isDisabled}
            onClick={(event) => {
              event.stopPropagation();

              if (isDisabled) return;

              if (isSelected) {
                onTogglePause(deckKey);
              } else {
                onOpen();
              }
            }}
          >
            {isSelected ? (
              isPaused ? (
                <img src={playIcon} alt="play" className="play" />
              ) : (
                <img src={stopIcon} alt="stop" className="stop" />
              )
            ) : (
              <span>+</span>
            )}
          </button>

          {!isSelected && <div className="deck-value">{value}</div>}

          {isSelected && (
            <div
              className="deck-actions"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <button
                type="button"
                className="deck-action"
                disabled={isDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  onOpen();
                }}
              >
                <img src={changeIcon} alt="change" />
                <span>WECHSELN</span>
              </button>

              <button
                type="button"
                className="deck-action"
                disabled={isDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove(deckKey);
                }}
              >
                <img src={deleteIcon} alt="delete" />
                <span>LÖSCHEN</span>
              </button>
            </div>
          )}
        </div>

        {isOpen && (
          <div className="deck-dropdown">
            <button
              type="button"
              className="close-icon"
              onClick={handleCloseDeckOptions}
            >
              <img src={closeIcon} alt="close" />
            </button>
            <p className="deck-drop-down-copy"> Track auswählen</p>
            {children}
          </div>
        )}
      </div>
    );
  }

  function TrackOption({ children, active, disabled, onClick }) {
    return (
      <button
        type="button"
        className={`track-option ${active ? "active" : ""}`}
        disabled={disabled || isSwitchingTempo || isUnlockingAudio}
        onClick={onClick}
      >
        {children}
        <i />
      </button>
    );
  }

  return (
    <section className={`game-page dj-console-page ${ident}`}>
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>

        <div className="console-wrapper">
          <div className="console-topbar">
            <div className="console-title">
              Main Channels (Decks)
              {((!isReady && loadingProgress > 0) ||
                isSwitchingTempo ||
                isUnlockingAudio) && (
                <span style={{ marginLeft: 12, opacity: 0.7 }}>
                  {isSwitchingTempo
                    ? `Przełączanie na ${tempo} BPM...`
                    : isUnlockingAudio
                      ? "Uruchamianie audio..."
                      : `Ładowanie: ${loadingProgress}%`}
                </span>
              )}
            </div>

            <button
              type="button"
              className="ghost-link underlined"
              onClick={() =>
                NiceModal.show("dj-console-modal", { content: "how-it-works" })
              }
            >
              SO FUNKTIONIERT'S
            </button>
          </div>

          <div className="decks-row" ref={decksAreaRef}>
            <DeckCircle
              deckKey="bass"
              title="BASS"
              value={getDeckLabel("bass")}
              active={!!selected.bass}
              isPaused={pausedDecks.bass}
              isOpen={openedDeck === "bass"}
              onOpen={() => handleOpenDeckOptions("bass")}
              onRemove={handleRemoveDeckSelection}
              onTogglePause={handleToggleDeckMute}
            >
              {tracks.bass.map((track) => (
                <TrackOption
                  key={track.id}
                  active={selected.bass === track.id}
                  onClick={() => handleDeckSelect("bass", track.id)}
                >
                  {track.name}
                </TrackOption>
              ))}
            </DeckCircle>

            <DeckCircle
              deckKey="kick"
              title="KICK"
              value={getDeckLabel("kick")}
              active={!!selected.kick}
              isPaused={pausedDecks.kick}
              isOpen={openedDeck === "kick"}
              onOpen={() => handleOpenDeckOptions("kick")}
              onRemove={handleRemoveDeckSelection}
              onTogglePause={handleToggleDeckMute}
            >
              {tracks.kick.map((track) => (
                <TrackOption
                  key={track.id}
                  active={selected.kick === track.id}
                  onClick={() => handleDeckSelect("kick", track.id)}
                >
                  {track.name}
                </TrackOption>
              ))}
            </DeckCircle>

            <DeckCircle
              deckKey="drums1"
              title="DRUMS"
              value={getDeckLabel("drums1")}
              active={!!selected.drums1}
              isPaused={pausedDecks.drums1}
              isOpen={openedDeck === "drums1"}
              onOpen={() => handleOpenDeckOptions("drums1")}
              onRemove={handleRemoveDeckSelection}
              onTogglePause={handleToggleDeckMute}
            >
              {tracks.drums.map((track) => (
                <TrackOption
                  key={track.id}
                  active={selected.drums1 === track.id}
                  disabled={isDrumsTrackDisabled("drums1", track.id)}
                  onClick={() => handleDeckSelect("drums1", track.id)}
                >
                  {track.name}
                </TrackOption>
              ))}
            </DeckCircle>

            <DeckCircle
              deckKey="drums2"
              title="DRUMS 2"
              value={getDeckLabel("drums2")}
              active={!!selected.drums2}
              isPaused={pausedDecks.drums2}
              isOpen={openedDeck === "drums2"}
              onOpen={() => handleOpenDeckOptions("drums2")}
              onRemove={handleRemoveDeckSelection}
              onTogglePause={handleToggleDeckMute}
            >
              {tracks.drums.map((track) => (
                <TrackOption
                  key={track.id}
                  active={selected.drums2 === track.id}
                  disabled={isDrumsTrackDisabled("drums2", track.id)}
                  onClick={() => handleDeckSelect("drums2", track.id)}
                >
                  {track.name}
                </TrackOption>
              ))}
            </DeckCircle>
          </div>

          <div className="sfx-section">
            <div className="sfx-head">
              <div className="sfx-label">
                SFX <span>{selected.sfx.length} / 4 ACTIVE</span>
              </div>
              <div className="sfx-hint">Erneut klicken zum Deaktivieren</div>
            </div>

            <div className="sfx-grid">
              {tracks.sfx.map((track) => {
                const active = selected.sfx.includes(track.id);
                const disabled = isSfxDisabled(track.id);

                return (
                  <button
                    key={track.id}
                    type="button"
                    className={`sfx-tile ${active ? "active" : ""}`}
                    disabled={disabled || isSwitchingTempo || isUnlockingAudio}
                    onClick={() => handleSfxToggle(track.id)}
                  >
                    <span>{track.name}</span>
                    <i />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="console-footer">
            <div className="tempo-box">
              <div className="tempo-label">
                TEMPO, BPM
                <img src={infoIcon} alt="info" />
              </div>

              <div className="tempo-scale">
                {BPM_OPTIONS.map((option) => {
                  const isActive = tempo === option;

                  return (
                    <button
                      key={option}
                      type="button"
                      className={`tempo-option ${isActive ? "active" : ""}`}
                      disabled={isSwitchingTempo || isUnlockingAudio}
                      onClick={() => handleTempoChange(option)}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              type="button"
              disabled={
                APIData.completed ||
                isSwitchingTempo ||
                isUnlockingAudio ||
                !(
                  selected.bass ||
                  selected.kick ||
                  selected.drums1 ||
                  selected.drums2 ||
                  selected.sfx.length > 0
                )
              }
              action={() =>
                NiceModal.show("dj-console-modal", {
                  content: "send",
                  sendAnswers,
                  setCurrentQuestion,
                  selected,
                  tempo,
                })
              }
            >
              {APIData.completed ? "Track Submitted" : "Submit Track"}
            </Button>

            <button
              type="button"
              className="ghost-link reset-link"
              onClick={resetAll}
            >
              ALLES ZURÜCKSETZEN
              <img src={repeatIcon} alt="repeat" className="reset-icon" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
